import React, { useState, memo, useMemo } from "react";
import { PaymentHistory } from "@/types/payment";
import { Product } from "@/types/product";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  History,
  Package,
  ChevronDown,
  Printer,
  Trash2,
  Calendar,
  Receipt,
  FileText,
  CheckCircle2,
} from "lucide-react";

interface PaymentHistoryTableProps {
  paymentHistory: PaymentHistory[];
  isLoadingPayments: boolean;
  products: Product[];
  onPrintPayment: (payment: PaymentHistory) => void;
  onDeletePayment: (payment: PaymentHistory) => Promise<void>;
  confirm: any;
}

export const PaymentHistoryTable = memo(function PaymentHistoryTable({
  paymentHistory,
  isLoadingPayments,
  products,
  onPrintPayment,
  onDeletePayment,
  confirm,
}: PaymentHistoryTableProps) {
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());

  const groupedPayments = useMemo(() => {
    return paymentHistory.reduce((groups, payment) => {
      const key = payment.productId;
      if (!groups[key]) {
        groups[key] = {
          productId: payment.productId,
          productName: payment.productName,
          currency: payment.currency,
          payments: [],
          productNotes: products.find(p => String(p.id) === String(payment.productId))?.notes || ""
        };
      }
      groups[key].payments.push(payment);
      return groups;
    }, {} as Record<string, { productId: string; productName: string; currency: "IQD" | "USD"; payments: PaymentHistory[]; productNotes: string }>);
  }, [paymentHistory, products]);

  const groupList = useMemo(() => Object.values(groupedPayments), [groupedPayments]);

  // پێشاندانی ماوە بە شێوەیەکی دروست و بیرکارییانە بەپێی کەمکردنەوە لە نرخی سەرەکی فرۆشتن
  const paymentRemainingMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const group of groupList) {
      const product = products.find(p => String(p.id) === String(group.productId));
      if (!product) continue;

      // پۆلێنکردنی پارەدانەکان لە کۆنەوە بۆ نوێ بۆ هەژمارکردنی دەقیقی بڕی ماوە
      const ascendingPayments = [...group.payments].sort((a, b) => {
        if (a.installmentNumber !== b.installmentNumber) {
          return a.installmentNumber - b.installmentNumber;
        }
        const dateA = new Date(a.date).getTime() || 0;
        const dateB = new Date(b.date).getTime() || 0;
        if (dateA !== dateB) return dateA - dateB;
        return (a.createdAt ? new Date(a.createdAt).getTime() : 0) - (b.createdAt ? new Date(b.createdAt).getTime() : 0);
      });

      let cumulative = 0;
      for (const p of ascendingPayments) {
        cumulative += p.amount;
        map.set(String(p.id), Math.max(0, product.salePrice - cumulative));
      }
    }
    return map;
  }, [groupList, products]);

  if (isLoadingPayments) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin mb-3"></div>
        <span className="text-sm">چاوەڕوان بە...</span>
      </div>
    );
  }

  if (paymentHistory.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <div className="h-16 w-16 rounded-full bg-muted/50 flex items-center justify-center mb-3">
          <History className="h-8 w-8 text-muted-foreground/50" />
        </div>
        <span className="text-sm">هیچ پارەدانێک نییە</span>
      </div>
    );
  }

  return (
    <>
      {/* Desktop Design */}
      <div className="hidden md:block" dir="rtl">
        <div className="flex items-center justify-between pb-3 mb-4">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <History className="h-4 w-4 text-primary" />
            </div>
            <span className="text-sm font-medium">مێژووی پارەدان</span>
          </div>
          <Badge variant="secondary" className="text-xs">
            {paymentHistory.length} پارەدان
          </Badge>
        </div>

        <div className="space-y-3">
          {groupList.map((group) => {
            const totalPaid = group.payments.reduce((sum, p) => sum + p.amount, 0);
            const totalInstallments = group.payments.length;
            const isExpanded = expandedProducts.has(`history_desktop_${group.productId}`);
            const product = products.find(p => String(p.id) === String(group.productId));

            const sortedPayments = [...group.payments].sort((a, b) => {
              if (b.installmentNumber !== a.installmentNumber) {
                return b.installmentNumber - a.installmentNumber;
              }
              const dateB = new Date(b.date).getTime() || 0;
              const dateA = new Date(a.date).getTime() || 0;
              if (dateB !== dateA) return dateB - dateA;
              return (b.createdAt ? new Date(b.createdAt).getTime() : 0) - (a.createdAt ? new Date(a.createdAt).getTime() : 0);
            });
            const maxInstallment = sortedPayments.length > 0 ? Math.max(...group.payments.map(p => p.installmentNumber)) : 0;
            const latestPayment = sortedPayments[0];

            // کۆتا بڕی ماوە دوای پارەدان
            const latestRemaining = product
              ? Math.max(0, product.salePrice - totalPaid)
              : (latestPayment?.remainingAfterPayment !== undefined
                ? latestPayment.remainingAfterPayment
                : undefined);

            return (
              <div
                key={group.productId}
                className={`rounded-xl border bg-card overflow-hidden transition-all shadow-sm hover:shadow-md ${isExpanded ? 'border-primary ring-2 ring-primary/20' : ''}`}
              >
                <button
                  className={`w-full px-4 py-3 transition-colors cursor-pointer ${isExpanded ? 'bg-primary/5' : 'hover:bg-muted/50'}`}
                  onClick={() => {
                    setExpandedProducts(prev => {
                      const newSet = new Set<string>();
                      const key = `history_desktop_${group.productId}`;
                      if (!prev.has(key)) {
                        newSet.add(key);
                      }
                      return newSet;
                    });
                  }}
                >
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center flex-shrink-0">
                        <Package className="h-5 w-5 text-primary" />
                      </div>
                      <div className="text-right min-w-0">
                        <p className="font-semibold text-base truncate">{group.productName}</p>
                        {group.productNotes && (
                          <p className="text-xs text-amber-600 truncate mt-0.5">{group.productNotes}</p>
                        )}
                      </div>
                    </div>

                    <div className={`grid ${product ? 'grid-cols-5' : 'grid-cols-4'} gap-4 flex-shrink-0`}>
                      {product && (
                        <div className="text-center">
                          <div className="text-xs text-muted-foreground">نرخی فرۆشتن</div>
                          <div className="text-sm font-semibold text-indigo-700 dark:text-indigo-400 mt-0.5" dir="ltr">
                            {product.salePrice.toLocaleString()}
                          </div>
                        </div>
                      )}
                      <div className="text-center">
                        <div className="text-xs text-muted-foreground">جار</div>
                        <div className="text-sm font-semibold mt-0.5">{totalInstallments}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-muted-foreground">وەرگیراو</div>
                        <div className="text-sm font-semibold text-emerald-600 mt-0.5" dir="ltr">
                          {totalPaid.toLocaleString()}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-muted-foreground">ماوە</div>
                        <div
                          className={`text-sm font-semibold mt-0.5 ${
                            latestRemaining === 0 ? 'text-emerald-600' : 'text-red-500'
                          }`}
                          dir="ltr"
                        >
                          {latestRemaining !== undefined ? latestRemaining.toLocaleString() : "-"}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-muted-foreground">دراوە</div>
                        <Badge variant="outline" className="text-xs mt-0.5">
                          {group.currency === "IQD" ? "د.ع" : "$"}
                        </Badge>
                      </div>
                    </div>

                    <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform duration-200 flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`} />
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t bg-muted/15 p-3 md:p-4">
                    <div className="rounded-xl border bg-background overflow-hidden shadow-xs">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/40 hover:bg-muted/40 border-b">
                            <TableHead className="text-right font-semibold text-xs py-3 w-28"># قیست</TableHead>
                            <TableHead className="text-right font-semibold text-xs py-3 w-32">بەروار</TableHead>
                            <TableHead className="text-right font-semibold text-xs py-3">بڕی پارەدان</TableHead>
                            <TableHead className="text-right font-semibold text-xs py-3">ماوە دوای پارەدان</TableHead>
                            <TableHead className="text-right font-semibold text-xs py-3 w-28">وەصڵ</TableHead>
                            <TableHead className="text-right font-semibold text-xs py-3">تێبینی</TableHead>
                            <TableHead className="text-center font-semibold text-xs py-3 w-24">کردارەکان</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {sortedPayments.map((payment, paymentIndex) => {
                            const isLastInstallment = payment.installmentNumber === maxInstallment;
                            const paymentDateObj = new Date(payment.date);
                            const remaining = paymentRemainingMap.get(String(payment.id)) ?? payment.remainingAfterPayment;

                            return (
                              <TableRow
                                key={payment.id}
                                className={`transition-colors border-b last:border-0 ${
                                  paymentIndex % 2 === 1 ? 'bg-muted/15' : 'bg-background'
                                } hover:bg-muted/30`}
                              >
                                {/* # قیست */}
                                <TableCell className="py-2.5">
                                  <div className="flex items-center gap-2">
                                    <span className="h-6 w-6 rounded-md bg-primary/10 text-primary font-bold text-xs flex items-center justify-center flex-shrink-0">
                                      {payment.installmentNumber}
                                    </span>
                                    <span className="text-xs font-semibold text-foreground/85">
                                      قیستی #{payment.installmentNumber}
                                    </span>
                                  </div>
                                </TableCell>

                                {/* بەروار */}
                                <TableCell className="py-2.5">
                                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
                                    <Calendar className="h-3.5 w-3.5 text-muted-foreground/60 flex-shrink-0" />
                                    <span dir="ltr">{paymentDateObj.toLocaleDateString("en-GB")}</span>
                                  </div>
                                </TableCell>

                                {/* بڕی پارەدان */}
                                <TableCell className="py-2.5">
                                  <div className="flex items-center gap-1 font-semibold text-sm text-emerald-600 dark:text-emerald-400">
                                    <span dir="ltr">{payment.amount.toLocaleString()}</span>
                                    <span className="text-xs font-normal text-muted-foreground">{group.currency === "IQD" ? "د.ع" : "$"}</span>
                                  </div>
                                </TableCell>

                                {/* ماوە دوای پارەدان */}
                                <TableCell className="py-2.5">
                                  {remaining !== undefined ? (
                                    <div
                                      className={`flex items-center gap-1 font-semibold text-sm ${
                                        remaining === 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-500 dark:text-red-400"
                                      }`}
                                    >
                                      {remaining === 0 ? (
                                        <>
                                          <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" />
                                          <span>0 {group.currency === "IQD" ? "د.ع" : "$"}</span>
                                        </>
                                      ) : (
                                        <>
                                          <span dir="ltr">{remaining.toLocaleString()}</span>
                                          <span className="text-xs font-normal text-muted-foreground">{group.currency === "IQD" ? "د.ع" : "$"}</span>
                                        </>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">-</span>
                                  )}
                                </TableCell>

                                {/* وەصڵ */}
                                <TableCell className="py-2.5">
                                  {payment.invoiceNumber ? (
                                    <Badge variant="outline" className="font-mono text-xs gap-1 py-0.5 px-2 bg-muted/40 border-muted-foreground/20">
                                      <Receipt className="h-3 w-3 text-muted-foreground" />
                                      <span>#{payment.invoiceNumber}</span>
                                    </Badge>
                                  ) : (
                                    <span className="text-xs text-muted-foreground/40">-</span>
                                  )}
                                </TableCell>

                                {/* تێبینی */}
                                <TableCell className="py-2.5 max-w-[200px]">
                                  {payment.note ? (
                                    <div className="flex items-center gap-1 text-xs bg-blue-50/80 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-md border border-blue-200/50 dark:border-blue-900/40 truncate" title={payment.note}>
                                      <FileText className="h-3 w-3 flex-shrink-0 opacity-70" />
                                      <span className="truncate">{payment.note}</span>
                                    </div>
                                  ) : (
                                    <span className="text-xs text-muted-foreground/40">-</span>
                                  )}
                                </TableCell>

                                {/* کردارەکان */}
                                <TableCell className="py-2.5">
                                  <div className="flex items-center justify-center gap-1">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground hover:bg-muted/70 rounded-md cursor-pointer"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onPrintPayment(payment);
                                      }}
                                      title="چاپکردنی وەصڵ"
                                    >
                                      <Printer className="h-4 w-4" />
                                    </Button>
                                    {isLastInstallment && (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-8 w-8 p-0 text-destructive/70 hover:text-destructive hover:bg-destructive/10 rounded-md cursor-pointer"
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          await confirm({
                                            title: "سڕینەوەی پارەدان",
                                            description: "دڵنیایت لە سڕینەوەی ئەم پارەدانە؟",
                                            variant: "destructive",
                                            confirmText: "بەڵێ، بیسڕەوە",
                                            cancelText: "نەخێر",
                                            onConfirm: async () => {
                                              await onDeletePayment(payment);
                                            }
                                          });
                                        }}
                                        title="سڕینەوە"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Mobile Design */}
      <div className="md:hidden space-y-2" dir="rtl">
        <div className="flex items-center justify-between pb-3 mb-2">
          <div className="flex items-center gap-2">
            <div className="h-6 w-6 rounded-md bg-primary/10 flex items-center justify-center">
              <History className="h-3 w-3 text-primary" />
            </div>
            <span className="text-xs font-medium">مێژووی پارەدان</span>
          </div>
          <Badge variant="secondary" className="text-[10px] h-5">
            {paymentHistory.length} پارەدان
          </Badge>
        </div>

        {groupList.map((group) => {
          const isExpanded = expandedProducts.has(`history_mobile_${group.productId}`);
          const totalPaid = group.payments.reduce((sum, p) => sum + p.amount, 0);
          const product = products.find(p => String(p.id) === String(group.productId));

          const sortedPayments = [...group.payments].sort((a, b) => {
            if (b.installmentNumber !== a.installmentNumber) {
              return b.installmentNumber - a.installmentNumber;
            }
            const dateB = new Date(b.date).getTime() || 0;
            const dateA = new Date(a.date).getTime() || 0;
            if (dateB !== dateA) return dateB - dateA;
            return (b.createdAt ? new Date(b.createdAt).getTime() : 0) - (a.createdAt ? new Date(a.createdAt).getTime() : 0);
          });
          const maxInstallment = sortedPayments.length > 0 ? Math.max(...group.payments.map(p => p.installmentNumber)) : 0;
          const latestPayment = sortedPayments[0];

          // کۆتا بڕی ماوە دوای پارەدان
          const latestRemaining = product
            ? Math.max(0, product.salePrice - totalPaid)
            : (latestPayment?.remainingAfterPayment !== undefined
              ? latestPayment.remainingAfterPayment
              : undefined);

          return (
            <div key={group.productId} className="rounded-lg border bg-card overflow-hidden">
              <button
                className="w-full p-3 text-right flex items-center justify-between hover:bg-muted/50 transition-colors cursor-pointer"
                onClick={() => {
                  setExpandedProducts(prev => {
                    const newSet = new Set<string>();
                    const key = `history_mobile_${group.productId}`;
                    if (!prev.has(key)) {
                      newSet.add(key);
                    }
                    return newSet;
                  });
                }}
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <Package className="h-4 w-4 text-primary flex-shrink-0" />
                  <div className="min-w-0 text-right">
                    <span className="font-semibold text-xs truncate block">{group.productName}</span>
                    <div className="flex items-center gap-2 text-[10px] text-muted-foreground mt-0.5 flex-wrap">
                      {product && (
                        <>
                          <span>نرخ: <span className="font-semibold text-foreground" dir="ltr">{product.salePrice.toLocaleString()}</span> {group.currency === "IQD" ? "د.ع" : "$"}</span>
                          <span>•</span>
                        </>
                      )}
                      <span>{group.payments.length} جار پارەدان</span>
                      {latestRemaining !== undefined && (
                        <>
                          <span>•</span>
                          <span className={latestRemaining === 0 ? "text-emerald-600 font-medium" : "text-red-500 font-medium"}>
                            ماوە: <span dir="ltr">{latestRemaining.toLocaleString()}</span> {group.currency === "IQD" ? "د.ع" : "$"}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="font-bold text-xs text-emerald-600">
                    <span dir="ltr">{totalPaid.toLocaleString()}</span> {group.currency === "IQD" ? "د.ع" : "$"}
                  </span>
                  <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                </div>
              </button>

              {isExpanded && (
                <div className="border-t bg-muted/20 p-2.5 space-y-2">
                  {sortedPayments.map((payment) => {
                    const isLastInstallment = payment.installmentNumber === maxInstallment;
                    const paymentDateObj = new Date(payment.date);
                    const remaining = paymentRemainingMap.get(String(payment.id)) ?? payment.remainingAfterPayment;

                    return (
                      <div
                        key={payment.id}
                        className="rounded-xl border bg-card p-3 text-xs space-y-2.5 shadow-xs hover:border-primary/30 transition-all"
                      >
                        {/* Header: Installment & Date & Actions */}
                        <div className="flex items-center justify-between gap-2 border-b pb-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="h-6 w-6 rounded-md bg-primary/10 text-primary font-bold text-xs flex items-center justify-center flex-shrink-0">
                              {payment.installmentNumber}
                            </span>
                            <span className="font-bold text-xs">قیستی #{payment.installmentNumber}</span>
                            <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                              <Calendar className="h-3 w-3 text-muted-foreground/60 flex-shrink-0" />
                              <span dir="ltr">{paymentDateObj.toLocaleDateString("en-GB")}</span>
                            </div>
                          </div>

                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground cursor-pointer"
                              onClick={() => onPrintPayment(payment)}
                              title="چاپکردن"
                            >
                              <Printer className="h-3.5 w-3.5" />
                            </Button>
                            {isLastInstallment && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10 border-destructive/30 cursor-pointer"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  await confirm({
                                    title: "سڕینەوەی پارەدان",
                                    description: "دڵنیایت لە سڕینەوەی ئەم پارەدانە؟",
                                    variant: "destructive",
                                    confirmText: "بەڵێ، بیسڕەوە",
                                    cancelText: "نەخێر",
                                    onConfirm: async () => {
                                      await onDeletePayment(payment);
                                    }
                                  });
                                }}
                                title="سڕینەوە"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* Financial Details Grid */}
                        <div className="grid grid-cols-2 gap-2 bg-muted/40 rounded-lg p-2 text-center" dir="rtl">
                          <div>
                            <span className="text-[10px] text-muted-foreground block mb-0.5">بڕی پارەدان</span>
                            <span className="font-bold text-xs text-emerald-600 dark:text-emerald-400" dir="ltr">
                              {payment.amount.toLocaleString()} {group.currency === "IQD" ? "د.ع" : "$"}
                            </span>
                          </div>
                          <div className="border-r border-border/50">
                            <span className="text-[10px] text-muted-foreground block mb-0.5">ماوە دوای پارەدان</span>
                            {remaining !== undefined ? (
                              <span
                                className={`font-bold text-xs ${
                                  remaining === 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-500"
                                }`}
                                dir="ltr"
                              >
                                {remaining.toLocaleString()} {group.currency === "IQD" ? "د.ع" : "$"}
                              </span>
                            ) : (
                              <span className="text-muted-foreground text-xs">-</span>
                            )}
                          </div>
                        </div>

                        {/* Footer Meta: Receipt & Note */}
                        {(payment.invoiceNumber || payment.note) && (
                          <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                            {payment.invoiceNumber ? (
                              <Badge variant="outline" className="font-mono text-[10px] gap-1 px-1.5 py-0.5 bg-muted/40">
                                <Receipt className="h-3 w-3 text-muted-foreground" />
                                <span>#{payment.invoiceNumber}</span>
                              </Badge>
                            ) : null}
                            {payment.note && (
                              <div className="text-[11px] bg-blue-50/80 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-md border border-blue-200/50 dark:border-blue-900/40 flex items-center gap-1 max-w-full truncate">
                                <FileText className="h-3 w-3 flex-shrink-0 opacity-70" />
                                <span className="truncate">{payment.note}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
});
